import PropTypes from 'prop-types';
import * as yup from 'yup';

import { trans, SoyuzFlag } from '@spotahome/soyuz/client';

import { useReCaptcha } from '../../../context/RecaptchaContext';

import FormProvider from '../../FormProvider';
import Button from '../../Button';

import LoginSignupModalTermsAndConditions from '../components/LoginSignupModalTermsAndConditions';
import LoginSignupModalToggle from '../components/LoginSignupModalToggle';

import { LOGIN, SIGNUP, HELP, FORGOTTEN_PASSWORD } from '../constants';

import styles from './LoginForm.module.scss';

const formSchema = yup.object().shape({
  email: yup
    .string()
    .ensure()
    .required('common.form.required-field')
    .email('lyp.validation.email.format'),
  password: yup.string().ensure().required('common.form.required-field')
});

const LoginForm = ({
  onToggleAction,
  onSubmit,
  error = null,
  disableLoginSignUpToggle = false,
  clearError,
  initialValues
}) => {
  const { executeRecaptcha, isRecaptchaReady, hasRecaptchaError } =
    useReCaptcha('login');

  const handleLoginSubmit = async ({ email, password }) => {
    const recaptchaToken = await executeRecaptcha();
    await onSubmit({ email, password }, recaptchaToken);
  };

  const handleToggleAction = () => {
    onToggleAction(SIGNUP);
  };

  const handleHelpForm = () => {
    onToggleAction(HELP);
  };

  const handleForgotPasswordClick = () => {
    onToggleAction(FORGOTTEN_PASSWORD);
  };

  return (
    <FormProvider
      onSubmit={handleLoginSubmit}
      schema={formSchema}
      initialValues={initialValues}
      fieldsClassName={styles['login-form__field']}
      disabled={!isRecaptchaReady}
    >
      <FormProvider.Input
        type="email"
        name="email"
        title={trans('loginsignup.modal.email')}
        dataTest="login-email"
        required
        autoFocus
        onBlurHandler={clearError}
      />

      <FormProvider.Password
        name="password"
        dataTest="login-password"
        title={trans('loginsignup.modal.password')}
        required
        onBlurHandler={clearError}
      />

      {hasRecaptchaError && (
        <p className={styles['login-form__error']}>
          {trans('tenant.recaptcha.error')}
        </p>
      )}

      <div className={styles['login-form__bottom-buttons']}>
        <Button
          color="link"
          onClick={handleForgotPasswordClick}
          data-test="loginSignupModal_forgotPassword"
        >
          {trans('traditional.login-form.forgot-password')}
        </Button>
        <SoyuzFlag name="showHelpFormInLogin">
          {flag =>
            flag.isOn() && (
              <Button
                color="link"
                onClick={handleHelpForm}
                className={styles['login-form__help-form']}
                data-test="loginSignupModal_helpForm"
              >
                {trans('traditional.login-form.help-form')}
              </Button>
            )
          }
        </SoyuzFlag>
      </div>

      <div className={styles['login-form__footer']}>
        <span className={styles['login-form__submit']}>
          <FormProvider.Submit>
            {trans('loginsignup.modal.continue')}
          </FormProvider.Submit>
        </span>
        {!!error && (
          <p
            data-test="login-form-error"
            className={styles['login-form__error']}
          >
            {error}
          </p>
        )}
        <span className={styles['login-form__terms-and-conditions']}>
          <LoginSignupModalTermsAndConditions action={LOGIN} />
        </span>
        {!disableLoginSignUpToggle && (
          <span className={styles['login-form__toggle']}>
            <LoginSignupModalToggle
              action={LOGIN}
              onToggleAction={handleToggleAction}
            />
          </span>
        )}
      </div>
    </FormProvider>
  );
};

LoginForm.propTypes = {
  onToggleAction: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.shape({
    handler: PropTypes.string,
    message: PropTypes.string
  }),
  disableLoginSignUpToggle: PropTypes.bool,
  initialValues: PropTypes.shape({
    email: PropTypes.string,
    password: PropTypes.string
  }).isRequired,
  clearError: PropTypes.func.isRequired
};

export default LoginForm;
